// src/components/ContactSidePanel.jsx
// Reusable right-side slide-in panel that shows ContactProfilePanel for a given phone number.
import React, { useEffect, useState, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import {
  Button,
  Icon,
  LoaderCircle,
} from 'rhinostyle';
import { fetchUser } from '../reducers/userReducer';
import { normalizePhone } from '../helpers/PhoneHelpers';

const ContactProfilePanel = React.lazy(() => import('./ContactProfilePanel'));

class SidePanelErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.warn('Side panel profile error:', error.message); // eslint-disable-line no-console
  }

  render() {
    if (this.state.hasError) {
      return <p className="u-text-center u-text-muted u-p-a">Unable to load contact profile</p>;
    }
    return this.props.children; // eslint-disable-line react/prop-types
  }
}

const ContactSidePanel = ({ phoneNumber, onClose }) => {
  const dispatch = useDispatch();
  const [contactId, setContactId] = useState(null);
  const [loading, setLoading] = useState(false);

  const fullUser = useSelector((state) => (contactId ? state.user.users[contactId] : null));
  const isFullUserLoaded = fullUser && fullUser.organization !== undefined;

  useEffect(() => {
    if (!phoneNumber) {
      setContactId(null);
      return;
    }

    const lookup = async () => {
      setLoading(true);
      try {
        const normalized = normalizePhone(phoneNumber);
        const response = await axios.get(`${process.env.REACT_APP_V3_BASE_URL}/voip/lookup/phone`, {
          params: { number: normalized },
        });
        const contact = response.data?.userrecord || response.data?.user || null;
        if (contact?.id) {
          setContactId(contact.id);
          dispatch(fetchUser(contact.id));
        } else {
          setContactId(null);
        }
      } catch (err) {
        setContactId(null);
      } finally {
        setLoading(false);
      }
    };

    lookup();
  }, [phoneNumber, dispatch]);

  const isOpen = !!phoneNumber;

  return (
    <>
      <div className={`call-panel ${isOpen ? 'is-open' : ''}`}>
        {isOpen && (
          <>
            <div className="call-panel__header">
              <span className="call-panel__header__phase">Contact</span>
              <Button type="link" iconOnly size="small" onClick={onClose} className="call-panel__header__close">
                <Icon icon="close" />
              </Button>
            </div>

            {loading && (
              <div className="u-text-center u-p-a"><LoaderCircle size="small" /></div>
            )}

            {!loading && contactId && isFullUserLoaded && (
              <div className="call-panel__profile">
                <SidePanelErrorBoundary>
                  <Suspense fallback={<div className="u-text-center u-p-a"><LoaderCircle size="small" /></div>}>
                    <ContactProfilePanel
                      activeUser={fullUser}
                      isActivePanel
                      showGoToConversation
                      setOpenContactLibraryPanel={() => {}}
                    />
                  </Suspense>
                </SidePanelErrorBoundary>
              </div>
            )}

            {!loading && contactId && !isFullUserLoaded && (
              <div className="u-text-center u-p-a"><LoaderCircle size="small" /></div>
            )}

            {!loading && !contactId && (
              <div className="call-panel__no-contact">
                <p className="u-text-center u-text-muted u-p-a">No contact found for this number</p>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ContactSidePanel;
